import type { WorkflowActivityChangeSetFieldMetadata } from "../../../api/types/workflowTypes";

export type InstructionSourceLocation = "StepInstructions" | "WorkflowInstructions";

export interface InstructionSource {
  quote?: string;
  explanation?: string;
  location?: InstructionSourceLocation;
}

export type QuoteRange = { start: number; end: number } | "notFound" | "notUnique";

const getNonBlankString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value : undefined;

const isInstructionSourceLocation = (value: unknown): value is InstructionSourceLocation =>
  value === "StepInstructions" || value === "WorkflowInstructions";

export const getInstructionSource = (
  metadata?: WorkflowActivityChangeSetFieldMetadata
): InstructionSource | undefined => {
  if (metadata?.Source !== "AdditionalAgentInstructions") {
    return undefined;
  }

  const quote = getNonBlankString(metadata.SourceInstructionQuote);
  const explanation = getNonBlankString(metadata.SourceExplanation);
  if (!quote && !explanation) {
    return undefined;
  }

  const location =
    quote && isInstructionSourceLocation(metadata.SourceInstructionLocation)
      ? metadata.SourceInstructionLocation
      : undefined;
  return {
    ...(quote ? { quote } : {}),
    ...(explanation ? { explanation } : {}),
    ...(location ? { location } : {}),
  };
};

interface CollapsedText {
  text: string;
  originalIndexes: number[];
}

const collapseWhitespace = (text: string): CollapsedText => {
  let collapsed = "";
  const originalIndexes: number[] = [];

  for (let i = 0; i < text.length; i++) {
    const isWhitespace = /\s/.test(text.charAt(i));
    if (isWhitespace && collapsed.endsWith(" ")) {
      continue;
    }

    collapsed += isWhitespace ? " " : text.charAt(i);
    originalIndexes.push(i);
  }

  return { text: collapsed, originalIndexes };
};

export const findQuoteRange = (text: string, quote: string): QuoteRange => {
  const normalizedQuote = collapseWhitespace(quote).text.trim();
  if (!normalizedQuote) {
    return "notFound";
  }

  const collapsed = collapseWhitespace(text);
  const start = collapsed.text.indexOf(normalizedQuote);
  if (start < 0) {
    return "notFound";
  }

  if (collapsed.text.indexOf(normalizedQuote, start + 1) >= 0) {
    return "notUnique";
  }

  const originalStart = collapsed.originalIndexes[start];
  const originalLast = collapsed.originalIndexes[start + normalizedQuote.length - 1];
  if (originalStart === undefined || originalLast === undefined) {
    return "notFound";
  }

  return { start: originalStart, end: originalLast + 1 };
};
